<template>
  <div class="personal-dashboard-container">
    <div class="dashboard-header" data-aos="fade-up">
      <h2 class="dashboard-title">个人状况</h2>
      <p class="dashboard-subtitle">实时展示我的当前状态和数据统计</p>
    </div>
    
    <div class="dashboard-grid">
      <!-- 在线状态卡片 -->
      <div class="status-card" data-aos="flip-left" data-aos-delay="100">
        <div class="card-header">
          <div class="status-indicator">
            <div :class="['status-dot', onlineStatus.class]"></div>
            <span class="status-text">{{ onlineStatus.text }}</span>
          </div>
          <div class="status-icon">🌐</div>
        </div>
        <div class="card-content">
          <h3 class="status-title">当前状态</h3>
          <p class="status-desc">{{ onlineStatus.description }}</p>
          <div class="status-time">
            <span>在线时长: {{ onlineTime }}</span>
          </div>
        </div>
      </div>
      
      <!-- 项目统计卡片 -->
      <div class="stats-card" data-aos="flip-left" data-aos-delay="200">
        <div class="card-header">
          <h3 class="card-title">项目统计</h3>
          <div class="card-icon">📊</div>
        </div>
        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-number">{{ animatedStats.projects }}</div>
            <div class="stat-label">完成项目</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">{{ animatedStats.commits }}</div>
            <div class="stat-label">代码提交</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">{{ animatedStats.studyDays }}</div>
            <div class="stat-label">学习天数</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">{{ animatedStats.codeLines }}k</div>
            <div class="stat-label">代码行数</div>
          </div>
        </div>
      </div>
      
      <!-- 最近活动 -->
      <div class="activity-card" data-aos="flip-left" data-aos-delay="300">
        <div class="card-header">
          <h3 class="card-title">最近活动</h3>
          <div class="card-icon">⚡</div>
        </div>
        <div class="activity-list">
          <div 
            v-for="(activity, index) in recentActivities" 
            :key="index"
            class="activity-item"
          >
            <div class="activity-icon" :style="{ backgroundColor: activity.color }">
              {{ activity.icon }}
            </div>
            <div class="activity-content">
              <div class="activity-title">{{ activity.title }}</div>
              <div class="activity-time">{{ activity.time }}</div>
            </div>
            <div class="activity-status" :class="activity.status">
              {{ activity.statusText }}
            </div>
          </div>
        </div>
      </div>
      
      <!-- 学习进度 -->
      <div class="progress-card" data-aos="flip-left" data-aos-delay="400">
        <div class="card-header">
          <h3 class="card-title">学习进度</h3>
          <div class="card-icon">📚</div>
        </div>
        <div class="progress-list">
          <div 
            v-for="progress in learningProgress" 
            :key="progress.name"
            class="progress-item"
          >
            <div class="progress-info">
              <span class="progress-name">{{ progress.name }}</span>
              <span class="progress-percent">{{ progress.percent }}%</span>
            </div>
            <div class="progress-bar">
              <div 
                class="progress-fill" 
                :style="{ 
                  width: progress.percent + '%',
                  backgroundColor: progress.color 
                }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PersonalDashboard',
  data() {
    return {
      onlineTime: '0小时0分钟',
      animatedStats: {
        projects: 0,
        commits: 0,
        studyDays: 0,
        codeLines: 0
      },
      targetStats: {
        projects: 25,
        commits: 1200,
        studyDays: 365,
        codeLines: 50
      },
      onlineStatus: {
        class: 'online',
        text: '在线',
        description: '正在专注学习新技术'
      },
      recentActivities: [
        {
          icon: '💻',
          title: '完成Vue3项目重构',
          time: '2小时前',
          status: 'completed',
          statusText: '已完成',
          color: '#4facfe'
        },
        {
          icon: '📖',
          title: '学习TypeScript高级特性',
          time: '5小时前',
          status: 'in-progress',
          statusText: '进行中',
          color: '#43e97b'
        },
        {
          icon: '🔧',
          title: '优化数据库查询性能',
          time: '1天前',
          status: 'completed',
          statusText: '已完成',
          color: '#667eea'
        }
      ],
      learningProgress: [
        { name: 'Vue.js 3.0', percent: 90, color: '#4fc08d' },
        { name: 'TypeScript', percent: 75, color: '#3178c6' },
        { name: 'Node.js', percent: 80, color: '#68a063' },
        { name: 'Docker', percent: 65, color: '#2496ed' },
        { name: 'Kubernetes', percent: 45, color: '#326ce5' }
      ]
    }
  },
  mounted() {
    // 确保DOM完全加载后再执行
    this.$nextTick(() => {
      setTimeout(() => {
        this.startOnlineTimer()
        this.animateStats()
      }, 100)
    })
  },
  beforeUnmount() {
    if (this.onlineTimer) {
      clearInterval(this.onlineTimer)
    }
  },
  methods: {
    startOnlineTimer() {
      let minutes = 0
      this.onlineTimer = setInterval(() => {
        minutes++
        const hours = Math.floor(minutes / 60)
        const mins = minutes % 60
        this.onlineTime = `${hours}小时${mins}分钟`
      }, 60000)
    },
    
    animateStats() {
      const duration = 2000
      const steps = 60
      const stepDuration = duration / steps
      
      Object.keys(this.targetStats).forEach(key => {
        const target = this.targetStats[key]
        const step = target / steps
        let current = 0
        
        const timer = setInterval(() => {
          current += step
          if (current >= target) {
            this.animatedStats[key] = target
            clearInterval(timer)
          } else {
            this.animatedStats[key] = Math.floor(current)
          }
        }, stepDuration)
      })
    }
  }
}
</script>

<style scoped>
.personal-dashboard-container {
  padding: 3rem 0;
  background: linear-gradient(135deg, rgba(67, 233, 123, 0.05) 0%, rgba(56, 249, 215, 0.05) 100%);
  border-radius: 20px;
  margin: 2rem 0;
}

.dashboard-header {
  text-align: center;
  margin-bottom: 3rem;
}

.dashboard-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-primary, #333);
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.dashboard-subtitle {
  font-size: 1.1rem;
  color: var(--text-secondary, #666);
  margin: 0;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.status-card,
.stats-card,
.activity-card,
.progress-card {
  background: var(--bg-secondary, white);
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-color, #e0e0e0);
  transition: all 0.3s ease;
}

.status-card:hover,
.stats-card:hover,
.activity-card:hover,
.progress-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.card-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary, #333);
  margin: 0;
}

.card-icon {
  font-size: 1.5rem;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.status-dot.online {
  background: #43e97b;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.status-text {
  font-weight: 600;
  color: #43e97b;
}

.status-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary, #333);
  margin: 0 0 0.5rem 0;
}

.status-desc {
  color: var(--text-secondary, #666);
  margin: 0 0 1rem 0;
}

.status-time {
  font-size: 0.9rem;
  color: var(--text-secondary, #666);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: #43e97b;
  margin-bottom: 0.3rem;
}

.stat-label {
  font-size: 0.9rem;
  color: var(--text-secondary, #666);
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--bg-tertiary, #f8f9fa);
  border-radius: 10px;
  transition: all 0.3s ease;
}

.activity-item:hover {
  transform: translateX(5px);
}

.activity-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
}

.activity-content {
  flex: 1;
}

.activity-title {
  font-weight: 600;
  color: var(--text-primary, #333);
  margin-bottom: 0.2rem;
}

.activity-time {
  font-size: 0.8rem;
  color: var(--text-secondary, #666);
}

.activity-status {
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
}

.activity-status.completed {
  background: rgba(67, 233, 123, 0.2);
  color: #43e97b;
}

.activity-status.in-progress {
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

.progress-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.progress-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.progress-name {
  font-weight: 600;
  color: var(--text-primary, #333);
}

.progress-percent {
  font-size: 0.9rem;
  color: var(--text-secondary, #666);
}

.progress-bar {
  height: 8px;
  background: var(--bg-tertiary, #f0f0f0);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 1s ease;
}

@media (max-width: 768px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
    padding: 0 1rem;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .activity-item {
    flex-direction: column;
    text-align: center;
  }
}
</style>
